import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WorkflowQuickStudyFinding, WorkflowRun } from '@digital-twins/shared-types'

import { ArtifactEditorShell } from '@/components/artifact-editor/ArtifactEditorShell'
import { reconcileArtifactBody } from '@/components/artifact-editor/reconcileArtifactBody'
import { CompareSegmentsGroup } from '@/components/quick-study-results/CompareSegmentsPicker'
import { buildDemographicCutGroups } from '@/components/quick-study-results/divergingStacked'
import { FindingServerSpecPlot } from '@/components/quick-study-results/featuredPlots/FindingServerSpecPlot'
import {
  analystSpecsFromPayload,
  hasFindingAnalystSpec,
} from '@/components/quick-study-results/featuredPlots/findingAnalystSpecs'
import {
  QuickStudyCutPlotsContext,
  quickStudyCutPlotsSourceFor,
} from '@/components/quick-study-results/quickStudyCutPlots'
import { Result } from '@/components/quick-study-results/quickStudyTypes'
import { StudyDocBody, StudyDocPrintButton } from '@/components/study-doc'
import { ContentLanguageProvider } from '@/i18n/contentLanguageContext'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import { composeThreadStudyDoc, populationIdFromRunInputs } from './composeThreadStudyDoc'
import { ArtifactPreviewCard } from './ThreadRunPane/ArtifactPreviewCard'
import { artifactDisplayLabel, localizeArtifactCategory } from './ThreadRunPane/artifactDisplayLabel'
import { studyDocArtifactFromRun, ThreadArtifactItem } from './ThreadRunPane/collectThreadArtifacts'
import { useThreadArtifactTips } from './ThreadRunPane/useThreadArtifactTips'

/**
 * What the twins actually said, IN THE THREAD.
 *
 * After Step 10 there is no Quick Study page to link to, so a run that spent
 * money has to report back where the reader already is: at the end of the
 * conversation, under the gate they approved. That is D2, and it is why this
 * renders in the reading column rather than in the run pane, which is for
 * watching progress.
 *
 * The write-up is THE POET StudyDoc inside `ArtifactPreviewCard` (Edit ·
 * Copy · Expand). InsightCard renders plots only, never the thread
 * narrative. A finding without `study_doc` degrades to digest or plot-only
 * — we do not fall back to card prose.
 *
 * WHY THE ROWS TRAVEL. `InsightCard` and `PlotRenderer` aggregate client-side
 * from `(question, responses)` — the distribution, the segment cuts, the theme
 * shares and every quote are computed in the browser from the response rows.
 * That is why D8 keeps the rows inline on the finding instead of capping them
 * or replacing them with a reference: a finding with no rows and no server spec
 * renders a headline and an empty chart. When a finding carries an analyst spec,
 * PlotRenderer prefers Step 7's adapter; rows stay for cut-switching and
 * dev-mode parity.
 *
 * DEGRADES, NEVER THROWS. A finding produced before the whole result was
 * carried has only the five summary fields, and a live run can hand over a
 * partial result — no narrative, or a verdict the grounding gate withheld.
 * Neither is an error state: the first falls back to the digest below, and the
 * second is the card's own normal rendering, since withholding an ungrounded
 * verdict is the grounding contract working.
 */

interface ThreadRunResultsProps {
  run: WorkflowRun | null
  className?: string
  /**
   * When the reader picks a cut, the attribute key (`segment_by`) is reported
   * so a parent can put it on the existing run-input bag.
   */
  onSegmentByChange?: (segmentBy: string | null) => void
  /**
   * Opens the full-viewport StudyDoc / artifact surface (ChatGPT Expand).
   */
  onExpandArtifact?: (item: ThreadArtifactItem) => void
}

/** One completed twin test on the run, with the findings it produced. */
interface ResultBlock {
  stepId: string
  stepIndex: number
  title: string
  panelSize: number
  findings: Array<WorkflowQuickStudyFinding>
}

/**
 * The result of a `quick_study` step, as a `Result` the card can render.
 *
 * The payload is python's own result object, carried verbatim by
 * `toTwinTestFinding` (C2), so it already IS this shape — the reads below only
 * fill the two fields the card needs and a partial frame may not carry. Nothing
 * is enumerated: the object is spread first, so a field python adds tomorrow
 * reaches the renderer without passing through here.
 */
export function toRenderableResult(finding: WorkflowQuickStudyFinding): Result | null {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  }

  const carried = payload as Record<string, unknown>
  const responses = Array.isArray(carried.responses) ? carried.responses : []
  const analystSpecs = analystSpecsFromPayload(carried)
  // A payload with no rows and no server spec cannot draw anything: the
  // charts aggregate from the rows, so the digest below is the honest
  // rendering, not an empty chart with a confident headline over it.
  // A spec with no rows still renders — the server is the numeric authority.
  if (responses.length === 0 && !hasFindingAnalystSpec(analystSpecs)) {
    return null
  }

  const plot =
    typeof carried.plot === 'string' && carried.plot.trim()
      ? carried.plot
      : hasFindingAnalystSpec(analystSpecs)
        ? 'bar'
        : carried.plot

  return {
    ...carried,
    // The gate's own wording for this question. The result carries a title too
    // in the ordinary case; this only supplies one when it does not, so the
    // exhibit is never labelled with an empty string.
    title: typeof carried.title === 'string' && carried.title.trim()
      ? carried.title
      : finding.question,
    headline:
      typeof carried.headline === 'string' && carried.headline.trim()
        ? carried.headline
        : finding.headline,
    plot,
    responses,
    analystCharts: analystSpecs.charts.length > 0 ? analystSpecs.charts : undefined,
    analystTables: analystSpecs.tables.length > 0 ? analystSpecs.tables : undefined,
  } as unknown as Result
}

/**
 * The five summary fields, for a finding that carries nothing else.
 *
 * Only reachable for a finding persisted before the whole result travelled, or
 * one whose result came back with no response rows. It says what is known and
 * stops: no chart, because there is nothing to aggregate, and no invented
 * confidence over an absence.
 */
function FindingDigest(props: {
  finding: WorkflowQuickStudyFinding
  position: number
}) {
  const { finding, position } = props

  const share =
    finding.topAnswerLabel && finding.topAnswerPercent > 0
      ? `${finding.topAnswerLabel} · ${Math.round(finding.topAnswerPercent)}%`
      : finding.topAnswerLabel

  return (
    <div
      className="rounded-xl border border-border bg-card p-3"
      data-testid={`thread-run-finding-digest-${position}`}
    >
      <Text size="xs" color="muted" className="leading-5">
        {finding.question}
      </Text>
      {finding.headline ? (
        <Heading level={3} size="sm" weight="medium" className="mt-1.5 leading-6">
          {finding.headline}
        </Heading>
      ) : null}
      {share ? (
        <Text
          size="sm"
          className="mt-1.5 leading-6 text-foreground"
          data-testid={`thread-run-finding-top-${position}`}
        >
          {share}
        </Text>
      ) : null}
      {finding.verbatim ? (
        <Text
          size="sm"
          color="muted"
          className="mt-2 leading-6"
          data-testid={`thread-run-finding-verbatim-${position}`}
        >
          &ldquo;{finding.verbatim}&rdquo;
        </Text>
      ) : null}
    </div>
  )
}

/** Every completed twin test on the run, in step order. */
export function collectResultBlocks(run: WorkflowRun): Array<ResultBlock> {
  const blocks: Array<ResultBlock> = []
  for (const step of run.steps) {
    // `outputOverride ?? output` for the same reason the gate reads it that
    // way: what was approved wins over the first draft.
    const output = step.outputOverride ?? step.output
    if (!output || output.kind !== 'quick_study') {
      continue
    }
    if (output.findings.length === 0) {
      continue
    }
    blocks.push({
      stepId: step.stepId,
      stepIndex: step.stepIndex,
      title: step.title,
      panelSize: output.panelSize,
      findings: output.findings,
    })
  }
  return blocks
}

function responsesFromFindings(
  findings: Array<WorkflowQuickStudyFinding>,
): Parameters<typeof buildDemographicCutGroups>[0] {
  const rows: Array<unknown> = []
  for (const finding of findings) {
    const payload = finding.result
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      continue
    }
    const responses = (payload as Record<string, unknown>).responses
    if (!Array.isArray(responses)) {
      continue
    }
    rows.push(...responses)
  }
  return rows as Parameters<typeof buildDemographicCutGroups>[0]
}

export function cutGroupsFromRun(
  run: WorkflowRun | null | undefined,
): Array<CompareSegmentsGroup> {
  if (!run) {
    return []
  }

  const blocks = collectResultBlocks(run)
  return buildDemographicCutGroups(
    responsesFromFindings(blocks.flatMap((block) => block.findings)),
  )
}

/**
 * Spec-only findings have no rows for InsightCard.hasData. StudyDocBody
 * already accepts a ReactNode plot lookup — render Step 7's adapter directly.
 */
export function plotLookupForResult(result: Result): Result | ReactNode {
  const charts = result.analystCharts ?? []
  const tables = result.analystTables ?? []
  if ((result.responses?.length ?? 0) > 0 || (charts.length === 0 && tables.length === 0)) {
    return result
  }

  return <FindingServerSpecPlot charts={charts} tables={tables} />
}

function contentLanguageFromRun(run: WorkflowRun): string {
  const raw = run.inputs?.language
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim()
  }
  return 'en'
}

export function ThreadRunResults(props: ThreadRunResultsProps) {
  const { run, className, onExpandArtifact } = props

  const { t } = useTranslation('app')

  const [isEditingStudyDoc, setIsEditingStudyDoc] = useState(false)

  const blocks = useMemo(() => (run ? collectResultBlocks(run) : []), [run])
  const studyDocItem = useMemo(() => studyDocArtifactFromRun(run), [run])
  const { tipsByIdentity, canEdit, upsertLocalTip } = useThreadArtifactTips(
    run?.token ?? null,
  )
  // W6-QS-c: the run every card's Compare-segments pick asks cut plots for.
  const cutPlotsSource = useMemo(() => quickStudyCutPlotsSourceFor(run), [run])

  if (!run) {
    return null
  }

  if (blocks.length === 0) {
    return null
  }

  const populationId = populationIdFromRunInputs(run.inputs)
  const studyDoc = composeThreadStudyDoc(
    blocks,
    toRenderableResult,
    null,
    populationId,
  )
  const studyDocPlots = Object.fromEntries(
    Object.entries(studyDoc.plots).map(([plotId, result]) => [plotId, plotLookupForResult(result)]),
  )
  const hasStudyDocMarkdown = studyDoc.markdown.trim().length > 0

  const digestFindings = blocks.flatMap((block) =>
    block.findings
      .map((finding, position) => ({
        finding,
        position,
        stepIndex: block.stepIndex,
      }))
      .filter(({ finding }) => !toRenderableResult(finding)),
  )

  const studyDocBody = (
    <>
      {hasStudyDocMarkdown ? (
        <div data-testid="thread-run-findings">
          <StudyDocBody
            markdown={studyDoc.markdown}
            plots={studyDocPlots}
            quotes={studyDoc.quotes}
            populationId={studyDoc.populationId}
            decisionConfidence={studyDoc.decisionConfidence}
            strips={studyDoc.strips}
            plotRegion="plot"
            layout="lite-magazine"
          />
        </div>
      ) : null}

      {digestFindings.map(({ finding, position, stepIndex }) => (
        <FindingDigest
          key={`digest-${stepIndex}-${position}`}
          finding={finding}
          position={position}
        />
      ))}
    </>
  )

  const tip = studyDocItem
    ? (tipsByIdentity.get(studyDocItem.key) ?? null)
    : null
  const displayBody = studyDocItem
    ? reconcileArtifactBody(studyDocItem.body, tip)
    : ''
  const studyDocLabel = studyDocItem
    ? artifactDisplayLabel({ ...studyDocItem, body: displayBody })
    : null
  const studyDocCategory = studyDocLabel
    ? localizeArtifactCategory(studyDocLabel.category, studyDocLabel.letter)
    : null
  const studyDocAccessibleName = studyDocLabel
    ? studyDocCategory
      ? `${studyDocCategory}: ${studyDocLabel.headline}`
      : studyDocLabel.headline
    : t('threads.detail.runPane.studyReport')
  const allowStudyDocEdit = Boolean(canEdit && studyDocItem)

  const framedStudyDoc = studyDocItem ? (
    isEditingStudyDoc && allowStudyDocEdit && studyDocItem.runToken ? (
      <div data-testid="thread-run-study-doc">
        <ArtifactEditorShell
          value={displayBody}
          accessibleName={studyDocAccessibleName}
          canEdit={allowStudyDocEdit}
          isEditing
          peek={false}
          original={tip?.original ?? true}
          onExpand={
            onExpandArtifact ? () => onExpandArtifact(studyDocItem) : undefined
          }
          onDone={() => setIsEditingStudyDoc(false)}
          onCancel={() => setIsEditingStudyDoc(false)}
          autosave={{
            runToken: studyDocItem.runToken,
            stepId: studyDocItem.stepId,
            artifactIdentity: studyDocItem.key,
            machineBody: studyDocItem.body,
            stepIndex: studyDocItem.stepIndex,
            existingTip: tip,
            onSaved: upsertLocalTip,
          }}
          className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3"
          testIdPrefix="artifact-preview-card"
        />
      </div>
    ) : (
      <div data-testid="thread-run-study-doc">
        <ArtifactPreviewCard
          item={studyDocItem}
          tip={tip}
          original={tip?.original ?? true}
          canEdit={allowStudyDocEdit}
          clamp={false}
          peek
          showHeader={false}
          onExpand={onExpandArtifact}
          headerExtra={
            <StudyDocPrintButton accessibleName={studyDocAccessibleName} />
          }
          onEdit={
            allowStudyDocEdit
              ? () => {
                  setIsEditingStudyDoc(true)
                }
              : undefined
          }
          className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3"
        >
          {studyDocBody}
        </ArtifactPreviewCard>
      </div>
    )
  ) : (
    <div
      className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3"
      data-testid="thread-run-study-doc"
    >
      {studyDocBody}
    </div>
  )

  return (
    <ContentLanguageProvider language={contentLanguageFromRun(run)}>
      <QuickStudyCutPlotsContext.Provider value={cutPlotsSource}>
        <div className={cn('space-y-4', className)} data-testid="thread-run-results">
          {framedStudyDoc}
        </div>
      </QuickStudyCutPlotsContext.Provider>
    </ContentLanguageProvider>
  )
}
